import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import { ClientSide } from "~/@types";
import { IInsight } from "~/@types/generated/contentful";
import { Email, Facebook, Linkedin, Twitter, Whatsapp } from "../icons";
import { CircleIconCta } from "../ctas";
import useIsMobile from "../utils/useIsMobile";

type InsightNavShareProps = {
  insight: ClientSide<IInsight>;
  className?: string;
};

export default function InsightNavShare({
  insight,
  className = "",
}: InsightNavShareProps) {
  const currentUrl =
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
      ? window.location.href
      : `https://institute.global/insights/${insight.fields.topic.fields.slug}/${insight.fields.slug}`;

  const emailSubject = insight.fields.title
    ? insight.fields.title.substring(0, 60)
    : "";
  const emailBody = insight.fields.title;
  const twitterTitle = `${insight.fields.title} via @InstituteGC`;

  const isMobile = useIsMobile(1024);
  const ctaKind = isMobile ? "black" : "white-hollow";
  const baseClassName =
    "lgmax:w-[24px] lgmax:h-[24px] lgmax:bg-transparent lgmax:border-0 lgmax:text-black";

  return (
    <div className={`flex gap-4 ${className}`}>
      <EmailShareButton
        data-component-type="social-link"
        data-social-platform="email"
        data-click-id={`share-email-${insight.fields.slug}`}
        data-module-title={insight.fields.title}
        url={currentUrl}
        subject={emailSubject}
        body={emailBody}
      >
        <CircleIconCta
          as="div"
          kind={ctaKind}
          icon={<Email className="h-4 w-4 text-gray-700" />}
          label="Share via Email"
          className={baseClassName}
        />
      </EmailShareButton>

      <LinkedinShareButton
        data-component-type="social-link"
        data-social-platform="linkedin"
        data-click-id={`share-linkedin-${insight.fields.slug}`}
        data-module-title={insight.fields.title}
        url={currentUrl}
        source={currentUrl}
      >
        <CircleIconCta
          as="div"
          kind={ctaKind}
          icon={<Linkedin className="h-4 w-4" />}
          label="Share on LinkedIn"
          className={baseClassName}
        />
      </LinkedinShareButton>

      <TwitterShareButton
        data-component-type="social-link"
        data-social-platform="x"
        data-click-id={`share-x-${insight.fields.slug}`}
        data-module-title={insight.fields.title}
        url={currentUrl}
        title={twitterTitle}
      >
        <CircleIconCta
          as="div"
          kind={ctaKind}
          icon={<Twitter className="h-4 w-4" />}
          label="Share with X"
          className={baseClassName}
        />
      </TwitterShareButton>

      <FacebookShareButton
        data-component-type="social-link"
        data-social-platform="facebook"
        data-click-id={`share-facebook-${insight.fields.slug}`}
        data-module-title={insight.fields.title}
        url={currentUrl}
      >
        <CircleIconCta
          as="div"
          kind={ctaKind}
          icon={<Facebook className="h-4 w-4 text-[#1877F2]" />}
          label="Share on Facebook"
          className={baseClassName}
          id={`share-link-facebook-${insight.fields.slug}`}
        />
      </FacebookShareButton>

      <WhatsappShareButton url={currentUrl}>
        <CircleIconCta
          kind={ctaKind}
          icon={
            <Whatsapp
              width={21}
              height={21}
              className={isMobile ? "text-black" : "text-white"}
            />
          }
          label="Share on WhatsApp"
          className={baseClassName}
        />
      </WhatsappShareButton>
    </div>
  );
}
